import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { chamar } from './api.ts';
import { useTextos } from './i18n.ts';
import { Consulta, Feedback, Segredo, useAcao } from './comum.tsx';
import { PERFIS_AGENTES } from './perfis-agentes.ts';

type Chave = {
  id: string;
  nome: string;
  prefixo: string;
  escopos: string[];
  estado: string;
  expira_em: string | null;
  ultimo_uso: string | null;
};
type Criada = {
  id: string;
  chave: string;
  snippets: Record<string, string>;
};
export function Chaves({ mcp = false }: {
  mcp?: boolean;
}) {
  const t = useTextos();
  const acao = useAcao();
  const [criada, setCriada] = useState<Criada | null>(null);
  const [rotacao, setRotacao] = useState<Chave | null>(null);
  // modelo de agente: so preenche nome e escopos, quem cria confere
  const [perfilId, setPerfilId] = useState('');
  const perfil = PERFIS_AGENTES.find((p) => p.id === perfilId);
  const q = useQuery({
    queryKey: ['integracoes', 'chaves'],
    queryFn: () => chamar<{
      chaves: Chave[];
    }>('/api/v1/integracoes/chaves'),
  });
  return <div className="integracoes">
    <Feedback acao={acao} />
    <form
      key={rotacao?.id ?? `nova-${perfilId}`}
      onSubmit={e => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        void acao.executar(async () => {
          const r = await chamar<Criada>('/api/v1/integracoes/chaves', {
            nome: f.get('nome'),
            escopos: String(f.get('escopos')).split(',').map(x => x.trim()).filter(Boolean),
            ambiente: f.get('ambiente'),
            expira_em: f.get('expira_em')
              ? new Date(String(f.get('expira_em'))).toISOString()
              : null,
          });
          setCriada(r);
          if (rotacao)
            await chamar(`/api/v1/integracoes/chaves/${rotacao.id}`, undefined, 'DELETE');
          setRotacao(null);
          return r;
        });
      }}
    >
      {!rotacao && <label>
        {t.modelo}
        <select value={perfilId} onChange={(e) => setPerfilId(e.target.value)}>
          <option value="">
            {t.semModelo}
          </option>
          {PERFIS_AGENTES.map((p) => <option key={p.id} value={p.id}>
            {p.nome} - {p.papel}
          </option>)}
        </select>
      </label>}
      <label>
        {t.nome}
        <input name="nome" required defaultValue={rotacao?.nome ?? perfil?.nome ?? ''} />
      </label>
      <label>
        {t.escopos}
        <input
          name="escopos"
          required
          defaultValue={rotacao?.escopos.join(', ')
            ?? perfil?.escopos.join(', ')
            ?? 'integracoes.ver'}
        />
      </label>
      <label>
        {t.ambiente}
        <select name="ambiente">
          <option value="test">
            {t.sandbox}
          </option>
          <option value="live">
            {t.producao}
          </option>
        </select>
      </label>
      <label>
        {t.expira}
        <input name="expira_em" type="datetime-local" />
      </label>
      <button disabled={acao.ocupado}>
        {rotacao ? t.rotacionar : t.criarChave}
      </button>
      {rotacao
        && <button type="button" onClick={() => setRotacao(null)}>
        {t.cancelar}
      </button>}
    </form>

    {criada
      && <>
      <Segredo valor={criada.chave} fechar={() => setCriada(null)} />
      {mcp
        && Object.entries(criada.snippets).map(([nome, texto]) => <section key={nome}>
        <h3>
          {nome}
        </h3>
        <Segredo valor={texto} fechar={() => setCriada(null)} />
      </section>)}
    </>}

    <Consulta consulta={q} vazio={!q.data?.chaves.length}>
      <div className="integracoes__tabela">
        <table>
          <thead>
            <tr>
              <th>
                {t.nome}
              </th>
              <th>
                {t.chave}
              </th>
              <th>
                {t.escopos}
              </th>
              <th>
                {t.expira}
              </th>
              <th>
                {t.detalhes}
              </th>
            </tr>
          </thead>
          <tbody>
            {q.data?.chaves.map(c => <tr key={c.id}>
              <td>
                {c.nome}
              </td>
              <td>
                {c.prefixo}
              </td>
              <td className="integracoes__escopos">
                {c.escopos.join(', ')}
              </td>
              <td>
                {c.expira_em ? new Date(c.expira_em).toLocaleString() : ''}
              </td>
              <td>
                {c.estado === 'ativa'
                  && <>
                  <button onClick={() => setRotacao(c)}>
                    {t.rotacionar}
                  </button>
                  <button
                    disabled={acao.ocupado}
                    onClick={() => void acao.executar(
                      () => chamar(
                        `/api/v1/integracoes/chaves/${c.id}`,
                        undefined,
                        'DELETE',
                      ),
                    )}
                  >
                    {t.revogar}
                  </button>
                </>}
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </Consulta>
  </div>;
}
